import { Car, ChevronRight, Home, Wrench, Zap } from "lucide-react";
import { Link } from "react-router-dom";

const categoryIcon = (name = "") => {
  const value = name.toLowerCase();
  if (value.includes("bil") || value.includes("cykel")) return Car;
  if (value.includes("el") || value.includes("elektr")) return Zap;
  if (value.includes("vvs") || value.includes("bolig") || value.includes("hjem")) return Home;
  return Wrench;
};

const cardTone = (problem) => {
  if (problem.status === "offer_accepted") return {
    border: "border-brand-line-strong",
    icon: "bg-[#E3F3E9] text-[#15805F]",
  };
  if (problem.clarification_unanswered) return {
    border: "border-[#F0CDA8]",
    icon: "bg-[#FFF0DD] text-[#E36D11]",
  };
  if ((problem.proposal_count || 0) > 0) return {
    border: "border-[#B9D5F2]",
    icon: "bg-[#E7F2FF] text-[#1769C2]",
  };
  return {
    border: "border-brand-line",
    icon: "bg-brand-soft text-brand-body",
  };
};

export default function CustomerRequestCard({ problem, category }) {
  const Icon = categoryIcon(category);
  const tone = cardTone(problem);
  const created = new Date(problem.created_at).toLocaleDateString("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return (
    <article className={`rounded-2xl border bg-white px-4 py-4 sm:px-5 ${tone.border}`} data-testid={`problem-card-${problem.id}`}>
      <div className="flex items-center gap-4">
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${tone.icon}`}>
          <Icon size={25} strokeWidth={1.8}/>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate font-display text-[16px] font-semibold text-brand-ink">{problem.title}</h3>
              <div className="mt-0.5 flex items-center gap-2 text-[12px] text-brand-muted">
                {category && <span>{category}</span>}
                {category && <span>·</span>}
                <span>Indsendt {created}</span>
              </div>
            </div>

            {problem.clarification_unanswered || ((problem.proposal_count || 0) > 0 && !["offer_accepted", "completed"].includes(problem.status)) ? (
              <Link to={`/forespoergsler/${problem.id}`} className="hidden shrink-0 items-center gap-1 rounded-lg bg-brand-fresh px-3 py-2 text-sm font-semibold text-white sm:inline-flex">
                Nyt svar · Se nu <ChevronRight size={16}/>
              </Link>
            ) : problem.status === "offer_accepted" ? (
              <span className="shrink-0 rounded-lg border border-brand-line-strong bg-brand-soft px-3 py-1.5 text-[11px] font-medium text-[#286443]">
                Ekspert valgt
              </span>
            ) : (
              <Link to={`/forespoergsler/${problem.id}`} className="hidden shrink-0 items-center gap-1 text-sm font-medium text-brand-green sm:inline-flex">
                Se forespørgsel <ChevronRight size={16}/>
              </Link>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {(problem.responded_count || 0) > 0 && <span className="rounded-md bg-[#E4F2E9] px-2.5 py-1 text-[11px] font-medium text-brand-fresh">
              {problem.responded_count} {problem.responded_count === 1 ? "svar fra ekspert" : "svar fra eksperter"}
            </span>}
            <span className="rounded-md bg-brand-soft px-2.5 py-1 text-[11px] text-brand-fresh">
              {problem.proposal_count || 0} prisforslag
            </span>
            {problem.clarification_unanswered && (
              <span className="rounded-md bg-[#FFF0DD] px-2.5 py-1 text-[11px] font-medium text-[#A85312]">Afventer dit svar</span>
            )}
            {problem.status === "offer_accepted" && (
              <span className="rounded-md bg-brand-soft px-2.5 py-1 text-[11px] font-medium text-[#286443]">Aktiv aftale</span>
            )}
          </div>

          {problem.accepted_expert_name && (
            <div className="mt-2 text-[12px] text-brand-muted">Valgt ekspert: {problem.accepted_expert_name}</div>
          )}
        </div>

        <Link to={`/forespoergsler/${problem.id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-brand-green hover:bg-brand-soft" aria-label="Åbn forespørgsel">
          <ChevronRight size={18}/>
        </Link>
      </div>
    </article>
  );
}

